/* global window, document, Promise */
import { Loader } from '@googlemaps/js-api-loader'
import { MarkerClusterer } from '@googlemaps/markerclusterer'


/**
 * Class for enscapsulating functionality for rendering and interacting with a map
 *
 */
export class GoogleMap {


	node
	infoWindowTemplate

	libLoader

	map
	markers = []
	clusterer
	activeInfoWindow


	/**
	 * Constructor
	 *
	 * @param {HTMLElement} node Node that map will be applied to
	 * @param {HTMLElement} infoWindowTemplate Template for map's info window
	 * 
	 */
	constructor( node, infoWindowTemplate ) {
		this.node = node
		this.infoWindowTemplate = infoWindowTemplate
	}


	/**
	 * Get API loader
	 * 
	 * Using getter/setter was causing errors, so we'll use class method for now
	 *
	 * @return {Loader}
	 */
	getLibLoader() {
		if( !this.libLoader ) {
			this.libLoader = new Loader({
				apiKey: window.webfxPropertyManagerData.mapApiKey
			})
		}

		return this.libLoader
	}


	/**
	 * Get map
	 *
	 * @return {Map}
	 */
	getMap() {
		return this.map
	}


	/**
	 * Create map
	 *
	 * @return {Promise<Map>}
	 */
	async createMap() {
		const loader = this.getLibLoader()

		const [ mapLib, coreLib ] = await Promise.all(
			[
				loader.importLibrary( 'maps' ),
				loader.importLibrary( 'core' ),
				loader.importLibrary( 'marker' ),
			]
		)

		const { Map, MapTypeControlStyle } = mapLib
		const { ControlPosition } = coreLib

		this.map = new Map( this.node, {
			mapId: this.node.getAttribute( 'id' ), // needed for advanced marker elements
			streetViewControl: false,
			fullscreenControl: false,
			mapTypeControlOptions: {
				position: ControlPosition.TOP_RIGHT,
				style: MapTypeControlStyle.HORIZONTAL_BAR,
			},
			zoom: 19,
			zoomControlOptions: {
				position: ControlPosition.LEFT_CENTER,
			},
		})

		return this.map
	}


	/**
	 * Add markers to map
	 * 
	 * @param array markerData
	 * @return {Promise<void>}
	 */
	async addMarkers( markerData ) {

		// close active info window when refreshing map
		if( this.activeInfoWindow ) {
			this.activeInfoWindow.close()
		}
		this.activeInfoWindow = null

		// remove preexisting markers
		this.resetMarkers()

		const [ coreLib, markerLib ] = await Promise.all(
			[
				this.getLibLoader().importLibrary( 'core' ),
				this.getLibLoader().importLibrary( 'marker' ),
			]
		)

		const { event, LatLng, LatLngBounds } = coreLib
		const { AdvancedMarkerElement } = markerLib

		const mapBounds = new LatLngBounds()

		markerData.forEach( item => {

			// create icon for pin
			const pinImg = document.createElement( 'img' )

			pinImg.src = window.webfxPropertyManagerData.mapPinIconUrl
			pinImg.setAttribute( 'width', 35 )
			pinImg.setAttribute( 'height', 51 )

			if( 0 === item.lat || 0 === item.lng ) {
				console.log( `Property does not have lat/lng values: "${item.id}"` )
				return false
			}

			const marker = new AdvancedMarkerElement({
				position: new LatLng( item.lat, item.lng ),
				content: pinImg,
				map: this.googleMap,
			})

			// open infowindow on click
			marker.addListener( 'click', this.handleMarkerClick.bind( this, marker, item ) )

			this.markers.push( marker )
			mapBounds.extend( marker.position )
		})

		this.map.fitBounds( mapBounds )
		event.addListener( this.map, 'zoom_changed', function() {
			if ( 17 < this.getZoom() ) {
				this.setZoom( 17 )
			}
		})

		this.clusterer = new MarkerClusterer({
			markers: this.markers,
			map: this.map,
		})

		// if only one marker, auto-open that infowindow for that marker
		if( 1 === this.markers.length ) {
			event.trigger( this.markers[0], 'click' )
		}
	}


	/**
	 * Populate infowindow on map
	 *
	 * @param {AdvancedMarkerElement} marker
	 * @param {object} item
	 * 
	 * @return {void}
	 */
	async handleMarkerClick( marker, item ) {
		const { InfoWindow } = await this.getLibLoader().importLibrary( 'maps' )

		// close preexisting info windows (if active)
		if( this.activeInfoWindow ) {
			this.activeInfoWindow.close()
		}
		this.activeInfoWindow = null

		this.activeInfoWindow = new InfoWindow({
			content: this.createContentForInfoWindow( item ),
		})

		this.activeInfoWindow.open({
			anchor: marker,
			map: this.map,
		})
	}


	/**
	 * Create content for info window
	 * 
	 * @todo Make this easier to use/apply
	 *
	 * @param {object} item
	 * @return {DocumentFragment}
	 */
	createContentForInfoWindow( item ) {
		const template = this.infoWindowTemplate.content.cloneNode( true )

		const nodeName = template.querySelector( '.js-info-marker-name' )
		if( nodeName && item.name ) {
			nodeName.textContent = item.name
		}

		const nodeAddress = template.querySelector( '.js-info-marker-address' )
		if( nodeAddress && item.address ) {
			nodeAddress.textContent = item.address
		}

		const nodeUrl = template.querySelector( '.js-info-marker-url' )
		if( nodeUrl && item.url ) {
			nodeUrl.setAttribute( 'href', item.url )
		}

		const nodeSalePrice = template.querySelector( '.js-info-marker-sale-price' )
		if( nodeSalePrice && item.salePrice ) {
			nodeSalePrice.textContent = item.salePrice
		}

		const nodeImg = template.querySelector( '.js-info-marker-img' )
		if( nodeImg && item.imageUrl ) {
			nodeImg.setAttribute( 'src', item.imageUrl )
		}

		return template
	}


	/**
	 * Reset all markers
	 *
	 * @return {void}
	 */
	resetMarkers() {
		if( this.markers.length ) {
			this.markers.forEach( marker => marker.setMap( null ) )
			this.markers = []
		}

		if( this.clusterer ) {
			this.clusterer.clearMarkers()
			this.clusterer = null
		}
	}
	

}